// RippleOne Design System
// Centralized design tokens, constants, and utilities

export const DESIGN_TOKENS = {
  // Spacing System - Balancer-inspired compact spacing
  spacing: {
    xs: 'space-y-2',
    sm: 'space-y-3', 
    md: 'space-y-4',
    lg: 'space-y-6',
    xl: 'space-y-8'
  },
  
  // Container System
  container: {
    sm: 'max-w-4xl',
    md: 'max-w-5xl', 
    lg: 'max-w-6xl',
    xl: 'max-w-7xl'
  },
  
  // Padding System
  padding: {
    xs: 'p-2 md:p-3',
    sm: 'p-4 md:p-6',
    md: 'p-6 md:p-8',
    lg: 'p-8 md:p-10',
    xl: 'p-10 md:p-12'
  },
  
  // Typography System
  typography: {
    // Compact typography for enterprise feel
    compact: {
      xs: 'text-compact-xs',
      sm: 'text-compact-sm', 
      base: 'text-compact-base',
      md: 'text-compact-md',
      lg: 'text-compact-lg',
      xl: 'text-compact-xl',
      '2xl': 'text-compact-2xl',
      '3xl': 'text-compact-3xl',
      '4xl': 'text-compact-4xl'
    },
    
    // Data typography for financial information
    data: {
      xs: 'text-data-xs',
      sm: 'text-data-sm',
      base: 'text-data-base', 
      md: 'text-data-md',
      lg: 'text-data-lg'
    },
    
    // Heading system - Balancer-inspired refined scale
    headings: {
      h1: 'text-5xl md:text-6xl lg:text-7xl font-semibold tracking-tight leading-[0.9]',
      h2: 'text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tight leading-[0.95]',
      h3: 'text-3xl md:text-4xl font-semibold tracking-tight leading-tight',
      h4: 'text-2xl md:text-3xl font-medium tracking-tight leading-tight',
      h5: 'text-xl md:text-2xl font-medium tracking-tight',
      h6: 'text-lg md:text-xl font-medium tracking-tight'
    },
    
    // Body text - More refined scale
    body: {
      xs: 'text-sm leading-relaxed',
      sm: 'text-base leading-relaxed',
      md: 'text-lg leading-relaxed',
      lg: 'text-xl leading-relaxed'
    },
    
    // Specialized text
    badge: 'text-sm font-semibold tracking-wide',
    caption: 'text-base md:text-lg leading-relaxed',
    label: 'text-sm font-medium tracking-wide',
    code: 'font-mono text-sm'
  },
  
  // Color System
  colors: {
    // Brand colors
    brand: {
      primary: 'text-ripple-blue',
      primaryLight: 'text-ripple-blue-light',
      secondary: 'text-ripple-teal',
      accent: 'text-accent-blue'
    },
    
    // Background colors
    backgrounds: {
      primary: 'bg-dark-bg',
      lighter: 'bg-dark-bg-lighter',
      darker: 'bg-dark-bg-darker',
      card: 'bg-dark-bg-lighter/80'
    },
    
    // Text colors
    text: {
      primary: 'text-white',
      secondary: 'text-ripple-gray-light',
      muted: 'text-ripple-gray',
      accent: 'text-ripple-blue'
    }
  },
  
  // Animation System
  animations: {
    // Timing
    timing: {
      fast: 'duration-200',
      normal: 'duration-300',
      slow: 'duration-500',
      slower: 'duration-700'
    },
    
    // Easing
    easing: {
      smooth: 'ease-in-out',
      bounce: 'ease-out',
      sharp: 'ease-in'
    },
    
    // Transforms
    transforms: {
      hover: 'hover:scale-105',
      press: 'active:scale-95',
      float: 'animate-float',
      pulse: 'animate-pulse-slow',
      shimmer: 'animate-shimmer'
    }
  }
};

// Component System
export const COMPONENTS = {
  // Card System
  cards: {
    sm: 'rounded-2xl bg-gradient-to-br from-dark-bg-lighter/80 to-dark-bg/80 backdrop-blur-md border border-white/10',
    md: 'rounded-3xl bg-gradient-to-br from-dark-bg-lighter/80 to-dark-bg/80 backdrop-blur-md border border-white/10',
    lg: 'rounded-3xl bg-gradient-to-br from-dark-bg-lighter/90 to-dark-bg/90 backdrop-blur-xl border border-white/15',
    glass: 'glass rounded-3xl',
    interactive: 'hover:border-ripple-blue/30 transition-all duration-300 group-hover:shadow-2xl group-hover:shadow-ripple-blue/10'
  },
  
  // Button System
  buttons: {
    primary: 'bg-gradient-to-r from-ripple-blue to-ripple-blue-light hover:from-ripple-blue/90 hover:to-ripple-blue-light/90 text-white font-semibold rounded-xl transition-all duration-300 shadow-lg shadow-ripple-blue/25 border border-ripple-blue/30',
    secondary: 'bg-white/5 hover:bg-white/10 backdrop-blur-md border border-white/20 hover:border-white/30 text-white font-semibold rounded-xl transition-all duration-300',
    outline: 'border border-white/40 text-white hover:bg-white/10 hover:border-white/60 rounded-xl transition-all duration-300',
    ghost: 'text-white hover:bg-white/5 rounded-xl transition-all duration-300'
  },
  
  // Button Sizes
  buttonSizes: {
    xs: 'px-3 py-1.5 text-xs',
    sm: 'px-4 py-2 text-sm',
    md: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg',
    xl: 'px-10 py-5 text-xl'
  },
  
  // Input System
  inputs: {
    base: 'bg-dark-bg-lighter/50 border border-white/20 rounded-xl px-4 py-3 text-white placeholder-ripple-gray focus:border-ripple-blue focus:ring-2 focus:ring-ripple-blue/20 transition-all duration-300',
    error: 'border-red-500 focus:border-red-500 focus:ring-red-500/20',
    success: 'border-green-500 focus:border-green-500 focus:ring-green-500/20'
  },
  
  // Badge System
  badges: {
    primary: 'bg-ripple-blue/20 text-ripple-blue px-3 py-1 rounded-full text-sm font-medium',
    secondary: 'bg-white/10 text-white px-3 py-1 rounded-full text-sm font-medium',
    success: 'bg-green-500/20 text-green-400 px-3 py-1 rounded-full text-sm font-medium',
    warning: 'bg-yellow-500/20 text-yellow-400 px-3 py-1 rounded-full text-sm font-medium',
    error: 'bg-red-500/20 text-red-400 px-3 py-1 rounded-full text-sm font-medium'
  },
  
  // Icon containers
  icons: {
    sm: 'w-8 h-8 rounded-lg bg-gradient-to-br from-ripple-blue/20 via-accent-blue/30 to-ripple-blue/20 flex items-center justify-center',
    md: 'w-10 h-10 rounded-xl bg-gradient-to-br from-ripple-blue/20 via-accent-blue/30 to-ripple-blue/20 flex items-center justify-center',
    lg: 'w-12 h-12 rounded-2xl bg-gradient-to-br from-ripple-blue/20 via-accent-blue/30 to-ripple-blue/20 flex items-center justify-center shadow-lg shadow-ripple-blue/20'
  }
};

// Layout System
export const LAYOUT = {
  // Page layouts
  page: {
    container: 'max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 xl:px-16',
    section: 'relative py-8 md:py-12 lg:py-16',
    grid: {
      '2col': 'grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8',
      '3col': 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8',
      '4col': 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6',
      '6col': 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-6'
    }
  },
  
  // Flex utilities
  flex: {
    center: 'flex items-center justify-center',
    between: 'flex items-center justify-between',
    start: 'flex items-center justify-start',
    end: 'flex items-center justify-end',
    col: 'flex flex-col',
    colCenter: 'flex flex-col items-center justify-center'
  }
};

// Theme System
export const THEME = {
  // Background effects
  backgrounds: {
    mesh: 'mesh-gradient',
    particle: 'particle-field',
    gradient: 'bg-gradient-to-br from-dark-bg via-dark-bg-lighter to-dark-bg-darker'
  },
  
  // Hover effects
  effects: {
    glow: 'hover:shadow-2xl hover:shadow-ripple-blue/20',
    lift: 'hover:transform hover:-translate-y-2',
    scale: 'hover:scale-105',
    shimmer: 'shimmer-gradient'
  },
  
  // Glass effects
  glass: {
    light: 'bg-white/5 backdrop-blur-md border border-white/10',
    medium: 'bg-white/10 backdrop-blur-lg border border-white/15',
    heavy: 'bg-white/15 backdrop-blur-xl border border-white/20'
  }
};

// Motion System (for Framer Motion)
export const MOTION = {
  // Common animations
  fadeInUp: {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  },
  
  fadeInScale: {
    initial: { opacity: 0, scale: 0.9 },
    animate: { opacity: 1, scale: 1 },
    transition: { duration: 0.4 }
  },
  
  slideInLeft: {
    initial: { opacity: 0, x: -30 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.5 }
  },
  
  // Hover animations
  hover: {
    scale: { scale: 1.05, transition: { duration: 0.2 } },
    lift: { y: -5, transition: { duration: 0.2 } },
    glow: { boxShadow: "0 25px 50px rgba(0, 118, 255, 0.4)", transition: { duration: 0.3 } }
  },
  
  // Stagger animations
  stagger: {
    container: {
      animate: {
        transition: {
          staggerChildren: 0.1
        }
      }
    },
    item: {
      initial: { opacity: 0, y: 20 },
      animate: { opacity: 1, y: 0 }
    }
  }
};

// Utility functions
export const utils = {
  // Combine classes utility
  cn: (...classes: (string | undefined | null | false)[]): string => {
    return classes.filter(Boolean).join(' ');
  },
  
  // Get responsive padding
  getResponsivePadding: (size: keyof typeof DESIGN_TOKENS.padding) => {
    return DESIGN_TOKENS.padding[size];
  },
  
  // Get container size
  getContainer: (size: keyof typeof DESIGN_TOKENS.container) => {
    return DESIGN_TOKENS.container[size];
  },
  
  // Get button classes
  getButton: (variant: keyof typeof COMPONENTS.buttons, size: keyof typeof COMPONENTS.buttonSizes = 'md') => {
    return `${COMPONENTS.buttons[variant]} ${COMPONENTS.buttonSizes[size]}`;
  }
};

// Export everything as default for easy importing
export default {
  DESIGN_TOKENS,
  COMPONENTS,
  LAYOUT,
  THEME,
  MOTION,
  utils
};

// Export everything for easy importing
export * from './components';
export * from './ThemeProvider';
export { default as DesignSystemComponents } from './components';
export { default as ThemeProvider } from './ThemeProvider'; 